/* viva-chat.component.ts  –  Option 1: outline first ➜ fill fades in */
import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  Input,
  ViewChildren,
  QueryList,
  ElementRef,
  HostListener,
  OnDestroy,
  EventEmitter,
  Output,
  ViewChild,
  OnInit
} from '@angular/core';
// Stub Capacitor for demo - no native functionality needed
const Capacitor = {
  getPlatform: () => 'web',
  isNativePlatform: () => false
};

const ImpactStyle = {
  Light: 'light',
  Medium: 'medium',
  Heavy: 'heavy'
};

const Haptics = {
  impact: async (options?: { style?: string }) => {},
  notification: async () => {},
  vibrate: async () => {}
};

const Network = {
  getStatus: async () => ({ connected: true, connectionType: 'wifi' }),
  addListener: () => ({ remove: () => {} })
};
import { Subscription } from 'rxjs';
import { EventService } from 'src/app/services/events/event.service';
import { StorageKey } from 'src/app/services/storage/models/storage-key.enum';
import { SecureStorageService } from 'src/app/services/storage/secure-storage.service';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';
// imports at top
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StoryContextService } from 'src/app/services/mock/story-context.service';

@Component({
  selector: 'viva-chat',
  templateUrl: './viva-chat.component.html',
  styleUrls: ['./viva-chat.component.scss'],
  standalone: false,

  animations: [
    trigger('bubbleFade', [
      transition(':enter', [   // fade in on *ngIf add
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 0.95 }))
      ]),
      transition(':leave', [   // fade out on *ngIf remove
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class VivaChatComponent implements OnInit, OnDestroy {
  @ViewChild('bubbleRef') bubbleRef?: ElementRef<HTMLDivElement>;
  private static bubbleWasShown = false;
  private _messageText?: string | null = undefined;
  private _lastMessageText?: string | null = undefined; // Store last message shown

  /* ────────── Public inputs ────────── */
  @Input() color = 'var(--ion-color-secondary)'; // stroke colour
  @Input() animationDuration = 3000;             // ms for outline
  @Input() fillColor = '#ffffff';                // mouth fill colour
  @Input() fillFadeTime = 2000;                  // ms for fade-in
  @Input() bubbleDuration = 5000;                // ms bubble visible
  @Input() interactive: boolean = true;
  @Input() showMoreInfoButton: boolean = false;   // Show/hide the more info button (default: false)
  @Input() customButtonText?: string;            // Custom button text (if not provided, uses default translation)
  @Input() useCustomButtonAction: boolean = false; // If true, emits onCustomButtonClick instead of onMoreInfoClick

  @Output() onMoreInfoClick = new EventEmitter<void>();
  @Output() onCustomButtonClick = new EventEmitter<void>(); // Custom button click event
  @Output() onBotClicked = new EventEmitter<string | null>(); // Emit message text if showing last message
  @Output() onCloseBubble = new EventEmitter<void>();

  /* ────────── Template refs ────────── */
  @ViewChildren('animatedPath', { read: ElementRef })
  private paths!: QueryList<ElementRef<SVGPathElement>>;

  public currentFace: VivaBotFaceState = VivaBotFaceState.happy;

  private faceTimer!: ReturnType<typeof setTimeout>;
  private faceTimout: any = null; // existing 15s reset timer
  private bubbleAutoCloseTimer: ReturnType<typeof setTimeout> | null = null; // 10s auto-close timer

  /* bubble flag bound with *ngIf in HTML */
  public showBubble = false;
  public static _isAwake = false;
  public isOnline: boolean = true;
  private subscriptions: Subscription[] = [];

  private greetingText: string = '';
  private hintText: string = '';
  private offlinetext: string = '';
  private onlineText: string = '';

  public textBublleLine1: string | null = null;
  public textBublleLine2: string | null = null;

  /* ────────── Transient face lock (prevents overrides) ────────── */
  private isFacePinned = false;

  private _speechEnabled = true;  // default ON
  public moreButtonText?: string;

  constructor(
    private host: ElementRef<HTMLElement>,
    private _TranslatorService: TranslatorService,
    private router: Router,
    private _storyContext?: StoryContextService
  ) {
    // Initialize with instant() - will be updated in ngOnInit if translations aren't ready
    this.greetingText = this._TranslatorService.instant('viva-chat-component.messages.greeting');
    this.hintText = this._TranslatorService.instant('viva-chat-component.messages.help-hint');
    this.offlinetext = this._TranslatorService.instant('viva-chat-component.messages.gone-offline');
    this.onlineText = this._TranslatorService.instant('viva-chat-component.messages.gone-online');
    this.moreButtonText = this._TranslatorService.instant('viva-chat-component.messages.more-info');

    /* Network status changes */
    this.subscriptions.push(
      EventService.networkStatusChange.subscribe(async (online: boolean) => {
        if (this.isFacePinned) {
          // Do not override thinking/error
          return;
        }
        setTimeout(async () => { //debounce network prompts
          const status = await Network.getStatus();
          if (status && status.connected != this.isOnline) {
            this.isOnline = status.connected;
            this.setFace(this.isOnline ? VivaBotFaceState.happy : VivaBotFaceState.sad);
            await this.showBubbleHint(this.isOnline ? this.onlineText : this.offlinetext, '');
          }
        }, 1000);
      })
    );

    /* External emotion changes */
    this.subscriptions.push(
      EventService.aiBotEmotionChange.subscribe(async (botFace: VivaBotFaceState) => {
        this.setFace(botFace);

        if (botFace === VivaBotFaceState.thinking) {
          this.textBublleLine1 = '';
          this.textBublleLine2 = null;
          this.showBubble = true;
        } else if (botFace === VivaBotFaceState.happy) {
          this.showBubble = false;
        }

        this.triggerResetTimer();
      })
    );

    this.subscriptions.push(this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => {
        this.resetForRouteChange();
      }));

    this.subscriptions.push(
      EventService.aiBotAwakeState.subscribe(async (isAwake: boolean) => {
        if (isAwake != VivaChatComponent._isAwake) {
          VivaChatComponent._isAwake = isAwake;
          if(VivaChatComponent._isAwake){
            await this.wakeupBot();
          }
        }
      })
    );
    
    // Subscribe to story context changes for gamification messages
    if (this._storyContext) {
      this.subscriptions.push(
        this._storyContext.getCurrentDay().subscribe(async (day) => {
          await this.updateFromStoryData();
        })
      );
      
      this.subscriptions.push(
        this._storyContext.getCurrentStoryId().subscribe(async () => {
          await this.updateFromStoryData();
        })
      );
    }
    
    // Subscribe to badge earned events
    this.subscriptions.push(
      EventService.badgeEarned.subscribe(async (badge: any) => {
        await this.handleBadgeEarned(badge);
      })
    );
  }
  
  /**
   * Maps story expression strings to VivaBotFaceState enum values
   */
  private mapExpressionToFaceState(expression: string): VivaBotFaceState {
    const normalized = expression.toLowerCase().trim();
    
    // Direct matches
    if (normalized === 'happy' || normalized === 'excited' || normalized === 'ecstatic' || 
        normalized === 'celebrating' || normalized === 'proud' || normalized === 'legendary' ||
        normalized === 'legend' || normalized === 'star-struck' || normalized === 'supportive' ||
        normalized === 'encouraging') {
      return VivaBotFaceState.happy;
    }
    
    if (normalized === 'sad' || normalized === 'concerned') {
      return VivaBotFaceState.sad;
    }
    
    if (normalized === 'thinking' || normalized === 'coaching') {
      return VivaBotFaceState.thinking;
    }
    
    if (normalized === 'error') {
      return VivaBotFaceState.error;
    }
    
    if (normalized === 'sleep') {
      return VivaBotFaceState.sleep;
    }
    
    // Default to happy if no match
    return VivaBotFaceState.happy;
  }

  private async updateFromStoryData(): Promise<void> {
    if (!this._storyContext) return;
    
    const storyData = this._storyContext.getCurrentStoryData();
    if (!storyData) return;
    
    // Update HeyViva expression from story data
    // Don't show thinking animation when just displaying a message - use happy instead
    if (storyData.heyVivaExpression) {
      const faceState = this.mapExpressionToFaceState(storyData.heyVivaExpression);
      // If the expression maps to thinking, use happy instead when just showing a message
      // (thinking should only be used when actually processing/looking up data)
      const finalFaceState = (faceState === VivaBotFaceState.thinking) 
        ? VivaBotFaceState.happy 
        : faceState;
      this.setFace(finalFaceState);
    } else {
      // Default to happy if no expression specified
      this.setFace(VivaBotFaceState.happy);
    }
    
    // Update message - only show if bot is awake and there's a message
    if (storyData.heyVivaMessage) {
      // Ensure bot is awake before showing message
      if (!VivaChatComponent._isAwake) {
        EventService.aiBotAwakeState.emit(true);
        // Wait a bit for the bot to wake up
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Use the messageText setter to trigger haptic and speech
      this.messageText = storyData.heyVivaMessage;
    }
  }
  
  private async handleBadgeEarned(badge: any): Promise<void> {
    // Set happy/celebrating expression
    this.setFace(VivaBotFaceState.happy);
    
    // Show celebration message using translation template
    const line1Key = 'viva-chat-component.gamification.badge-earned.line1';
    const line2Key = 'viva-chat-component.gamification.badge-earned.line2';
    
    let line1 = this._TranslatorService.translateText(line1Key);
    let line2 = this._TranslatorService.translateText(line2Key);
    
    // Replace parameters in translated text
    if (line1 && badge.name) {
      line1 = line1.replace('{{badgeName}}', badge.name);
    }
    
    await this.showBubbleHint(line1, line2);
  }


  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((a) => a.unsubscribe());
    }
    // Clear auto-close timer on destroy
    this.clearBubbleAutoCloseTimer();
    //TextToSpeech.stop().catch(() => { });
  }

  private triggerResetTimer() {
    this.replayBubbleAnim();

    if (this.faceTimout) {
      clearTimeout(this.faceTimout);
      this.faceTimout = null;
    }
    this.faceTimout = setTimeout(() => {
      // Don’t override if pinned in thinking/error
      if (this.isFacePinned) return;

      this.setFace(this.isOnline ? VivaBotFaceState.happy : VivaBotFaceState.sad);
      this.showBubble = false;

      if (this.faceTimout) {
        clearTimeout(this.faceTimout);
        this.faceTimout = null;
      }
    }, 15000);
  }

  private isTransient(face: VivaBotFaceState) {
    return face === VivaBotFaceState.thinking || face === VivaBotFaceState.error;
  }

  private setFace(face: VivaBotFaceState) {
    this.currentFace = face;
    this.isFacePinned = this.isTransient(face);
  }

  get isAwake():boolean{
    return VivaChatComponent._isAwake;
  }

  /* ────────── Lifecycle ────────── */
  async ngOnInit(): Promise<void> {
    const status = await Network.getStatus();
    this.isOnline = status.connected;

    this._speechEnabled = true;
    const audioEnabled = await SecureStorageService.get(StorageKey.AIBotAutoEnabled);
    if (audioEnabled && audioEnabled == 'false') {
      this._speechEnabled = false;
    }

    // Ensure translations are loaded and update text values
    this._TranslatorService.get([
      'viva-chat-component.messages.greeting',
      'viva-chat-component.messages.help-hint',
      'viva-chat-component.messages.gone-offline',
      'viva-chat-component.messages.gone-online',
      'viva-chat-component.messages.more-info'
    ]).subscribe((translations: any) => {
      this.greetingText = translations['viva-chat-component.messages.greeting'] || this.greetingText;
      this.hintText = translations['viva-chat-component.messages.help-hint'] || this.hintText;
      this.offlinetext = translations['viva-chat-component.messages.gone-offline'] || this.offlinetext;
      this.onlineText = translations['viva-chat-component.messages.gone-online'] || this.onlineText;
      this.moreButtonText = translations['viva-chat-component.messages.more-info'] || this.moreButtonText;
    });

    this.initialisePaths();
    this.startDrawingLoop();
    // start your 30s "no-interaction" timer:
    this.startFaceTimer();

    // Update from story data on initial load
    if (this._storyContext) {
      // Small delay to ensure everything is initialized
      setTimeout(async () => {
        await this.updateFromStoryData();
      }, 100);
    }
  }

  private async wakeupBot(){
    // Only show messages when there's an active story/journey
    // Don't show generic greeting on welcome page or when no story is active
    if (this._storyContext) {
      const storyId = this._storyContext.getCurrentStoryIdValue();
      if (!storyId) {
        // No active story - don't show any messages
        return;
      }
      // There's an active story - let updateFromStoryData() handle showing story-specific messages
      return;
    }

    // If no story context service, don't show messages
    // (This shouldn't happen in normal operation, but safety check)
    return;
  }

  private async showBubbleHint(line1: string, line2: string): Promise<void> {
    this.textBublleLine1 = line1;
    this.textBublleLine2 = line2;
    this.showBubble = true;
    let textToRead = line1 + ' ' + line2;

    if (this._speechEnabled) {
      await this.speakText(textToRead);
    }
    await this.triggerHaptic();

    // Start auto-close timer (10 seconds)
    this.startBubbleAutoCloseTimer();
  }


  get speechEnabled(): boolean {
    return this._speechEnabled;
  }

  set speechEnabled(value: boolean) {
    this._speechEnabled = value;
    if (!value) {
      this.stopAudio();
    } else {
      if (this._messageText) {
        this.speakText(this._messageText);
      }
    }
    if (value) {
      SecureStorageService.set(StorageKey.AIBotAutoEnabled, 'true');
    } else {
      SecureStorageService.set(StorageKey.AIBotAutoEnabled, 'false');
    }
  }

  private async triggerHaptic() {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: 'light' });
      } catch (err) {
        console.warn('Haptics not available', err);
      }
    }

  }


  @Input()
  set messageText(value: string | null | undefined) {
    this._messageText = value;
    
    // Store the last message if it's not null/undefined and has content
    if (value && value.length > 0) {
      this._lastMessageText = value;
    }

    if (value && value.length > 0) {
      // Bubble will show because of *ngIf binding
      this.triggerHaptic();
      this.speakText(value);
      // Start auto-close timer (10 seconds)
      this.startBubbleAutoCloseTimer();
    } else {
      // Clear timer if message is cleared
      this.clearBubbleAutoCloseTimer();
    }
  }
  get messageText(): string | null | undefined {
    return this._messageText;
  }

  /* ────────── Stroke-drawing setup ────────── */
  private initialisePaths(): void {
    const totalPaths = this.paths.length;
    const segmentTime = this.animationDuration / totalPaths;

    this.paths.forEach((el, index) => {
      const path = el.nativeElement;
      const length = path.getTotalLength();

      /* base styling */
      path.style.stroke = this.color;
      path.style.fill = this.fillColor;
      path.style.fillOpacity = '1'; // hidden at start
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;

      /* stroke animation */
      path.style.transition = `stroke-dashoffset ${segmentTime}ms ease-out ${segmentTime * index}ms`;
    });
  }

  @HostListener('click')
  onUserInteract() {
    // If they touch the bot, flip back to happy (unless pinned) and restart timer
    if (!this.isFacePinned) {
      this.showHappy();
    }
    this.startFaceTimer();
  }

  private startFaceTimer() {
    // clear any old timer
    clearTimeout(this.faceTimer);
    // after 30 000 ms of no taps, go to the sleep face (unless pinned)
    this.faceTimer = setTimeout(() => {
      if (this.isFacePinned) return;
      this.setFace(VivaBotFaceState.sleep);
    }, 30_000);
  }

  private showHappy() {
    // go immediately back to happy (unless pinned)
    if (this.isFacePinned) return;
    this.setFace(VivaBotFaceState.happy);
  }

  /* ────────── Animation loop ────────── */
  private startDrawingLoop(): void {
    this.playAnimationOnce();
    setInterval(
      () => this.playAnimationOnce(),
      this.animationDuration + 50 /* safety buffer */
    );
  }

  private playAnimationOnce(): void {
    if (!this.isVisible(this.host.nativeElement)) return;

    const segmentTime = this.animationDuration / this.paths.length;

    /* 1️⃣ draw the strokes */
    requestAnimationFrame(() => {
      this.paths.forEach(el => (el.nativeElement.style.strokeDashoffset = '0'));
    });

    /* 2️⃣ after outline is complete, fade in the fill */
    setTimeout(() => {
      if (!this.isVisible(this.host.nativeElement)) return;

      this.paths.forEach(el => {
        const path = el.nativeElement;
        path.style.transition += `, fill-opacity ${this.fillFadeTime}ms ease-in`;
        path.style.fillOpacity = '1';
      });
    }, this.animationDuration);

    /* 3️⃣ reset for next loop */
    setTimeout(() => {
      if (!this.isVisible(this.host.nativeElement)) return;

      this.paths.forEach((el, index) => {
        const path = el.nativeElement;
        const length = path.getTotalLength();

        path.style.transition = 'none';
        path.style.strokeDashoffset = `${length}`;
        path.style.fillOpacity = '0';
        /* force reflow */
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        path.getBoundingClientRect();

        path.style.transition = `stroke-dashoffset ${segmentTime}ms ease-out ${segmentTime * index}ms`;
      });
    }, this.animationDuration + this.fillFadeTime + 30);
  }

  private isVisible(el: Element | null | undefined): boolean {
    if (!el || !(el as any).isConnected) return false;
    const s = getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;
    if ((el as Element).getClientRects().length === 0) return false;
    return true;
  }

  public async closeMiniChat() {
    this.clearBubbleAutoCloseTimer(); // Clear auto-close timer when manually closed
    this.showBubble = false;
    this.messageText = null; // Clear message text to hide the bubble (use setter)
   // await TextToSpeech.stop(); // stop previous
    this.replayBubbleAnim();
    this.onCloseBubble.emit();
  }

  public gotoFullChat() {
    this.showBubble = false;
    this.replayBubbleAnim();
    this.onMoreInfoClick.emit();
  }

  public onButtonClick() {
    if (this.useCustomButtonAction) {
      this.onCustomButtonClick.emit();
    } else {
      this.gotoFullChat();
    }
  }

  /**
   * Check if there's a handler for the button click
   * Only show button if there's a subscriber to one of the events
   */
  get hasButtonHandler(): boolean {
    if (this.useCustomButtonAction) {
      return this.onCustomButtonClick.observers.length > 0;
    } else {
      return this.onMoreInfoClick.observers.length > 0;
    }
  }

  public botClicked() {
    this.replayBubbleAnim();
    
    // If there was a last message, show it again
    if (this._lastMessageText && this._lastMessageText.length > 0) {
      // Show the last message
      this.messageText = this._lastMessageText;
      // Emit the last message so parent can sync miniChatText
      this.onBotClicked.emit(this._lastMessageText);
    } else {
      // No last message - emit null so parent can set generic message
      this.onBotClicked.emit(null);
    }
  }

  private replayBubbleAnim() {
    if (this.bubbleRef && this.bubbleRef.nativeElement) {
      const el = this.bubbleRef.nativeElement;
      el.style.animation = 'none'; // remove animation
      // force reflow so the browser registers the change
      void el.offsetWidth;
      el.style.animation = '';     // restore CSS (or set explicitly: 'fadeIn 1s ease-in')
    }
  }

  private async resetForRouteChange(): Promise<void> {
    try {
     // await TextToSpeech.stop(); // stop any ongoing TTS
    } catch { } // be tolerant if plugin is not ready

    // Clear bubble/message state
    this.clearBubbleAutoCloseTimer(); // Clear auto-close timer
    this.showBubble = false;
    this._messageText = null;
    this.textBublleLine1 = null;
    this.textBublleLine2 = null;

    // Unlock & return to steady state face
    this.isFacePinned = false;
    this.setFace(this.isOnline ? VivaBotFaceState.happy : VivaBotFaceState.sad);

    // Clear any pending timers
    if (this.faceTimout) {
      clearTimeout(this.faceTimout);
      this.faceTimout = null;
    }

    // Optionally restart the idle/sleep timer
    this.startFaceTimer();

    // If bubble was mid-animation, ensure it doesn't flicker back in
    this.replayBubbleAnim();
  }

  private stopAudio() {
   /* setTimeout(async () => {
      await TextToSpeech.stop(); // stop previous
    }, 10);*/
  }

  private async speakText(text: string) {

    return; /*

    if (!this.speechEnabled || !text) return;
    const platform = Capacitor.getPlatform();

    // Language hint from your translator service if available
    const lang = this.getDefaultBrowserLang();

    await TextToSpeech.stop(); // stop previous

    const plainText = this.htmlToSpeechText(text);
    

    await TextToSpeech.speak({
      text: plainText,
      lang,
      rate: 1.2,
      pitch: 1.0,
      volume:0.5,
      category: 'ambient',
      queueStrategy: QueueStrategy.Flush
    });*/
  }

  private getDefaultBrowserLang(): string {
    // `navigator.language` gives e.g. "en-US", "nl-NL", "fr"
    // `navigator.languages` is an array of preferred languages
    if (navigator.languages && navigator.languages.length > 0) {
      return navigator.languages[0];
    }
    return navigator.language || 'en-US'; // fallback
  }


  private htmlToSpeechText(html: string): string {
    if (!html) return '';

    // 1) insert line breaks where it makes sense
    let normalized = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n');

    // 2) let the browser parse & decode entities safely
    const tmp = document.createElement('div');
    tmp.innerHTML = normalized;

    // 3) extract text and tidy whitespace
    const text = (tmp.textContent || tmp.innerText || '')
      .replace(/\u00A0/g, ' ')     // nbsp -> space
      .replace(/[ \t]+\n/g, '\n')  // trim end-of-line spaces
      .replace(/\n{3,}/g, '\n\n')  // collapse big gaps
      .trim();

    return text;
  }
  public toggleSpeech() {
    this.speechEnabled = !this.speechEnabled;
  }

  /**
   * Starts a 10-second timer to automatically fade out the bubble if not manually closed
   */
  private startBubbleAutoCloseTimer(): void {
    // Clear any existing timer first
    this.clearBubbleAutoCloseTimer();

    // Set new timer to fade out after 10 seconds
    this.bubbleAutoCloseTimer = setTimeout(() => {
      // Only auto-close if bubble is still visible
      if (this.showBubble || (this._messageText && this._messageText.length > 0)) {
        this.showBubble = false;
        this._messageText = null;
        VivaChatComponent.bubbleWasShown = true;
      }
      this.bubbleAutoCloseTimer = null;
    }, 10000); // 10 seconds
  }

  /**
   * Clears the auto-close timer (called when user manually closes the bubble)
   */
  private clearBubbleAutoCloseTimer(): void {
    if (this.bubbleAutoCloseTimer) {
      clearTimeout(this.bubbleAutoCloseTimer);
      this.bubbleAutoCloseTimer = null;
    }
  }
}

export enum VivaBotFaceState {
  happy = 'happy',
  sad = 'sad',
  error = 'error',
  sleep = 'sleep',
  thinking = 'thinking'
}